import { useCallback, useEffect, useRef, useState } from 'react';

/** `value`, but only after it has stopped changing for `ms` (use for search boxes). */
export function useDebouncedValue(value, ms = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

/**
 * "Latest request wins". `begin()` aborts the previous request and returns
 * `{ signal, current() }`: pass `signal` to the API call and only touch state
 * while `current()` is true, so a slow old response can never overwrite a newer
 * one. Everything in flight is aborted on unmount.
 */
export function useLatestRequest() {
  const ref = useRef(null);
  useEffect(() => () => ref.current?.abort(), []);
  return useCallback(() => {
    ref.current?.abort();
    const ctrl = new AbortController();
    ref.current = ctrl;
    return { signal: ctrl.signal, current: () => ref.current === ctrl };
  }, []);
}

/**
 * Runs `fn` now, then every `ms` while the tab is visible. It is skipped while
 * the tab is hidden and runs once as soon as the tab is shown again. A new `fn`
 * (e.g. changed filters) restarts the cycle with an immediate run.
 */
export function usePolling(fn, ms, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    const run = () => {
      if (!document.hidden) fn();
    };
    run();
    const t = setInterval(run, ms);
    document.addEventListener('visibilitychange', run);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', run);
    };
  }, [fn, ms, enabled]);
}
